/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */

import {compareAsc, parseISO} from 'date-fns';
import moment from 'moment';
import {error} from '@ion/core/src/logger/logger';
import {
  AccumulatorType,
  Device,
  GenericObject,
  IonTagReportsType,
  ScReportsType,
  TimeSeriesType
} from '../types/entities';
import {genericUnitConversion} from '../utils/Conversions';

interface GenericTimeSeriesReportsConversionParams {
  report: GenericObject[];
  deviceDataConverted?: GenericObject;
  systemOfUnits: string;
  settings: GenericObject;
};

export const parseDateString = (
  dateTime: string,
  referenceTimezone = null,
  preferenceTimeFormat = 'h:mm A'
) => {
  return moment.utc(dateTime, 'YYYY-MM-DD HH:mm:ss').tz(referenceTimezone).format(preferenceTimeFormat);
};

export const compareDates = (currentDate: string, nextDate: string, referenceTimezone= null) => {
  compareAsc(
    parseISO(currentDate),
    parseISO(nextDate)
  );
};

/**
 * Utility function to get the threhsold max & min values for light or temperature
 * i.e. maxTemperature, minTemperature, maxLightLevel, minLightLevel from
 * the accumulator array for the perticular loco event
 *
 */
const getThresholdValues = (label: string, accumulators: GenericObject) => {
  const threshold = {max: undefined, min: undefined};
  const setThreshold = (minLabel: string, maxlabel: string) => {
    accumulators.some(({label, value}) => {
      if (label === minLabel) {
        threshold.min = value;
      } else if (label === maxlabel) {
        threshold.max = value;
      }
      return !!(threshold.max && threshold.min);
    });
  };
  if (label === 'temperature') {
    setThreshold('minTemperature', 'maxTemperature');
  } else if (label === 'lightLevel') {
    setThreshold('minLightLevel', 'maxLightLevel');
  }
  return threshold;
};

const isTagReportInstance = (obj: any[]): obj is IonTagReportsType[] =>
  obj?.length && 'tagReport' in obj[0];

const genericTimeSeriesReportsConversion = ({
  report,
  deviceDataConverted,
  systemOfUnits,
  settings
}: GenericTimeSeriesReportsConversionParams) => {
  try {
    const {timeSeriesData, accumulators} = deviceDataConverted || {};
    const startIndex = report.length;
    timeSeriesData?.forEach((timeSeries: TimeSeriesType, index: number) => {
      const {value, time, units, label} = timeSeries;
      const valueNumber = genericUnitConversion(
        parseFloat(value),
        units,
        systemOfUnits
      );
      if (time) {
        report.push({
          key       : startIndex + index,
          y         : valueNumber,
          x         : time,
          value     : valueNumber,
          eventTime : time,
          threshold : getThresholdValues(label, accumulators),
          units,
          systemOfUnits,
          settings
        });
      }
    });
  } catch (e) {
    error('error in parsing timeSeriesData');
  }
  return report;
};

const genericReportsConversion = ({
  acc,
  deviceDataConverted,
  eventTime,
  messageUuid
},
{
  timeZone,
  timeFormat,
  systemOfUnits,
  settings
}
) => {
  try {
    const {accumulators} = deviceDataConverted || {};
    accumulators.forEach((accumulator: AccumulatorType, index: number) => {
      const {label, value, units} = accumulator;
      const valueNumber = genericUnitConversion(parseFloat(value), units, systemOfUnits);
      if (!(label in acc)) {
        acc[label] = {
          startTime : parseDateString(eventTime, timeZone, timeFormat),
          endTime   : parseDateString(eventTime, timeZone, timeFormat),
          minVal    : valueNumber,
          maxVal    : valueNumber,
          data      : [{
            eventTime,
            value,
            units,
            systemOfUnits,
            settings,
            y         : valueNumber,
            x         : eventTime,
            threshold : getThresholdValues(label, accumulators)
          }]
        };
      } else {
        const {minVal, maxVal} = acc[label];
        if (index === accumulators?.length - 1) {
          acc[label].endTime = parseDateString(eventTime, timeZone, timeFormat);
        }
        acc[label].minVal = minVal > valueNumber
          ? valueNumber
          : minVal;
        acc[label].maxVal = maxVal < valueNumber
          ? valueNumber
          : maxVal;
        acc[label].data?.push({
          eventTime,
          value,
          units,
          settings,
          systemOfUnits,
          y         : valueNumber,
          x         : eventTime,
          threshold : getThresholdValues(label, accumulators)
        });
      }
    });
  } catch (e) {
    error(`error in parsing reports for ${messageUuid}`);
  }
  return acc;
};

const tagReportConversion = (tagReports: IonTagReportsType[], {timeZone, timeFormat, systemOfUnits, settings}) => {
  if (tagReports?.length) {
    const results = {
      currentReport : tagReports[0]
    };
    let accumulatedResults = null;
    accumulatedResults = tagReports.reduce(
      (acc, {tagReport: {deviceDataConverted, eventTime, messageUuid}}: IonTagReportsType) => {
        return genericReportsConversion(
          {acc, deviceDataConverted, eventTime, messageUuid},
          {timeZone, timeFormat, systemOfUnits, settings}
        );
      }, {});
    const timeSeriesDataResults = tagReports.reduce(
      (report, {tagReport: {deviceDataConverted}}: IonTagReportsType) => {
        return genericTimeSeriesReportsConversion(
          {
            report,
            deviceDataConverted,
            systemOfUnits,
            settings
          }
        );
      },
      []
    );

    return {
      ...results,
      accumulatedResults,
      timeSeriesDataResults
    };
  }
  return {
    currentReport         : {},
    accumulatedResults    : [],
    timeSeriesDataResults : []
  };
};

const deviceReportConversion = (deviceReports: ScReportsType[], {timeZone, timeFormat, systemOfUnits, settings}) => {
  if (deviceReports?.length) {
    const results = {
      currentReport : deviceReports[0]
    };
    let accumulatedResults = null;
    accumulatedResults = deviceReports.reduce(
      (acc, {locoEvent: {deviceDataConverted, eventTime, messageUuid}}: ScReportsType) => {
        return genericReportsConversion(
          {acc, deviceDataConverted, eventTime, messageUuid},
          {timeZone, timeFormat, systemOfUnits, settings}
        );
      }, {});
    const timeSeriesDataResults = deviceReports.reduce(
      (report, {locoEvent: {deviceDataConverted}}: ScReportsType) => {
        return genericTimeSeriesReportsConversion(
          {
            report,
            deviceDataConverted,
            systemOfUnits,
            settings
          }
        );
      },
      []
    );

    return {
      ...results,
      timeSeriesDataResults,
      accumulatedResults
    };
  }
  return {
    currentReport         : {},
    accumulatedResults    : [],
    timeSeriesDataResults : []
  };
};

export const convertReportsToDetails = (tagReports: IonTagReportsType[] | ScReportsType[],
  {timeZone, timeFormat, systemOfUnits, settings}) => {
  if (isTagReportInstance(tagReports)) {
    return tagReportConversion(tagReports, {timeZone, timeFormat, systemOfUnits, settings});
  } else {
    return deviceReportConversion(tagReports, {timeZone, timeFormat, systemOfUnits, settings});
  }
};

/**
 * The `resolveDeviceName` utility provides a simple convenience function which will
 * return the `name` to be used when referencing a `Device` name based on resolving
 * the first property which provides the name value, beginning with the default
 * `name` attribute, and falling back to the `deviceName` attribute if necessary and
 * based on the `fallback` value returns other unique identifier values if name and
 * deviceName is not available.
 *
 * ...
 * <describe scenarios as to how and why a device may have a different attribute
 * used for name>
 * ...
 *
 * ```typescript
 *   import {resolveDeviceName} from `@ion/core/src/entities/Device`;
 *
 *   const name = resolveDeviceName(device); // either device.name, or device.deviceName
 *
 * ```
 * @param device A device instance from which to resolve it's name value
 * @param fallback if true utility checks the device.name or device.devicename is available
 * @returns The resolved device name, either `name` or falling back to `deviceName` or
 *          based on `fallback` returns other unique identifier values.
 */
export const resolveDeviceName = (device: Device, fallback: boolean = false): string | number => {
  let result = device?.name || device?.deviceName;
  if (!result && fallback) {
    result = device?.esn || device?.imei || device?.serialNumber || device?.id;
  }
  return result;
};
